// Extended per-type editorial content: hair, makeup, height, face, plus-size.
// Rendered as dedicated sections on /kibbe/[type] pages.

export interface KibbeTypeExtended {
  hair: {
    intro: string;
    bestStyles: { name: string; why: string }[];
    avoid: string;
  };
  makeup: {
    intro: string;
    tips: string[];
  };
  height: {
    range: string;
    detail: string;
  };
  face: {
    intro: string;
    tell: string;
  };
  plusSize: {
    intro: string;
    styling: string[];
  };
  palette: { name: string; hex: string }[];
  heightBar: { minCm: number; maxCm: number };
}

export const kibbeExtended: Record<string, KibbeTypeExtended> = {
  dramatic: {
    hair: {
      intro:
        "Dramatic hair should do what Dramatic clothing does: hold a line. Your bone structure is architectural, and hair that's precise and intentional amplifies it, while soft, diffused styles blur the very sharpness that makes you striking. Length matters less than edges — whatever the cut, it should look decided.",
      bestStyles: [
        { name: "Blunt one-length cut", why: "A single hard hemline (bob or long) mirrors your clean vertical line" },
        { name: "Long, glass-straight with center part", why: "Extends the vertical and frames angular features symmetrically" },
        { name: "Sharp pixie or undercut crop", why: "Exposes the jawline and cheekbones instead of hiding them" },
        { name: "Slicked-back low bun", why: "The severity most types can't pull off reads as elegance on you" },
      ],
      avoid:
        "Loose beachy waves, heavy layering, and soft curtain bangs work against you — they add a randomness your lines don't have. If you want movement, keep it heavy and controlled (think one deep S-wave, not tousled texture).",
    },
    makeup: {
      intro:
        "Think editorial, not pretty. Dramatic faces can carry makeup that would overwhelm nearly everyone else, and they look underdone in the no-makeup makeup that flatters Naturals.",
      tips: [
        "Sculpt, don't soften: use contour to sharpen cheekbones rather than blush to round them",
        "A strong, structured brow anchors the whole face — keep tails crisp",
        "Matte bold lips (deep red, oxblood, even vamp shades) sit beautifully on thin, straight lips",
        "Graphic eyeliner — a true wing or floating liner — echoes your angularity where soft smoky blur does not",
      ],
    },
    height: {
      range: "Typically 5'7\" (170 cm) and above",
      detail:
        "Dramatics carry a long, unbroken vertical line, and most are genuinely tall. A shorter woman can occasionally be a Dramatic if her proportions read long and narrow, but if you're 5'4\" and considering Dramatic, look hard at Flamboyant Gamine or Dramatic Classic first — vertical line is about the impression of length, and small scale usually breaks it.",
    },
    face: {
      intro:
        "The Dramatic face is all edges: a defined jawline that photographs from every angle, prominent cheekbones, a straight (often long) nose, and narrow or elongated eyes. Lips tend to be on the thinner, straighter side. There is very little roundness anywhere — even smiling, the face keeps its geometry.",
      tell:
        "The tell against neighboring types: zero blunt width (that's Flamboyant Natural) and zero moderation (that's Dramatic Classic). If strangers describe your face as \"striking\" or \"severe\" before they say \"pretty,\" you're in Dramatic territory.",
    },
    plusSize: {
      intro:
        "Weight does not change bone structure, and Dramatic bones stay visibly angular at any size. A plus-size Dramatic usually keeps a comparatively flat hip-to-shoulder line and gains weight evenly rather than in pronounced curves — which means the long-line strategy still works, it just needs better fabric.",
      styling: [
        "Keep the unbroken vertical: long duster coats, column dresses, matched monochrome separates",
        "Choose fabrics with body (heavy crepe, gabardine, ponte) — thin knits ripple where structure should be",
        "Sharp necklines (deep V, high funnel) keep the eye moving up and down, not across",
        "Skip the plus-size default of ruching and wrap details — added softness fights your line instead of flattering it",
      ],
    },
    palette: [
      { name: "Charcoal", hex: "#3B3B3F" },
      { name: "True Red", hex: "#B11226" },
      { name: "Cool Taupe", hex: "#8E8489" },
      { name: "Porcelain", hex: "#EFDFD3" },
      { name: "Deep Plum", hex: "#5A2A42" },
    ],
    heightBar: { minCm: 170, maxCm: 190 },
  },

  "soft-dramatic": {
    hair: {
      intro:
        "Soft Dramatic hair has one job: match your scale. You have a long line plus lush curve, and small, timid hair genuinely unbalances the picture — the classic SD complaint of \"my head looks too small for my body\" is almost always a hair problem, not a body problem.",
      bestStyles: [
        { name: "Old-Hollywood waves", why: "Large, sculpted S-waves carry both your drama and your yin at once" },
        { name: "Long layers with real volume", why: "Big hair balances a grand scale; flat lengths look draining" },
        { name: "Voluminous side-swept blowout", why: "Asymmetric sweep adds glamour without fussiness" },
        { name: "Full, glossy curls (natural or set)", why: "Curve in the hair echoes curve in the body — keep it defined, not fluffy" },
      ],
      avoid:
        "Skip skinny flat-ironed lengths, wispy shags, and short crops that expose more frame than they balance. If you cut short, keep serious volume and polish — a small, plain pixie makes an SD look bottom-heavy.",
    },
    makeup: {
      intro:
        "Full glam was practically invented for this type. Soft Dramatics have large, lush features that hold saturated color, and \"toned down\" often just reads as tired on them.",
      tips: [
        "Play up the lips — yours are likely full, and a bold cream lipstick is your one-step glamour move",
        "Winged liner plus visible lashes suit large eyes; skip barely-there mascara",
        "Warm sculpting under cheekbones, then a deliberate highlight — SDs can carry shimmer that overwhelms Classics",
        "Jewel tones and deep berries beat dusty pastels, which disappear against your feature intensity",
      ],
    },
    height: {
      range: "Typically 5'5\"–5'10\" (165–178 cm)",
      detail:
        "Soft Dramatics need a dominant vertical line, so most sit at moderate-tall to tall heights. Sophia Loren (5'9\") is the archetype. If you're 5'3\" with lush curves and sharp facial bones, you're far more likely a Theatrical Romantic — the two types share yin flesh, but TR is small-scale where SD is grand.",
    },
    face: {
      intro:
        "The Soft Dramatic face pairs a strong underlying structure — defined jaw, prominent cheekbones — with unmistakably lush detail: full lips, large liquid eyes, soft cheeks. It's the combination that confuses people: sharp frame, ripe features. Neither half tells the story alone.",
      tell:
        "The tell: bone sharpness plus flesh fullness in the same face. A Romantic face is soft on soft; a Dramatic face is sharp on sharp. If your jawline is decisive but your lips and eyes are lavish, that exact contradiction is Soft Dramatic.",
    },
    plusSize: {
      intro:
        "Soft Dramatic may be the type that changes least visually with weight — the silhouette was always long-plus-curvy, and it stays long-plus-curvy. Weight typically settles in the bust and hips while the vertical line keeps doing its work, which is why so many famous plus-size style icons are typed SD.",
      styling: [
        "Drape, don't tent: wrap dresses, surplice necklines, and bias cuts follow curve while a boxy cover-up erases your best asset",
        "Keep the neckline open and low — it maintains the vertical and showcases an SD's proportions",
        "Go bigger with accessories as your scale goes up; dainty jewelry disappears on a grand frame",
        "A defined (not cinched) waist keeps the hourglass legible — belts in soft, wide materials work better than stiff narrow ones",
      ],
    },
    palette: [
      { name: "Champagne", hex: "#E7C9A9" },
      { name: "Bronze", hex: "#A5673F" },
      { name: "Deep Raspberry", hex: "#8E3B60" },
      { name: "Terracotta", hex: "#C97B63" },
      { name: "Espresso", hex: "#5D4037" },
    ],
    heightBar: { minCm: 165, maxCm: 178 },
  },

  "flamboyant-natural": {
    hair: {
      intro:
        "Flamboyant Natural hair should look like it happened on the way to somewhere interesting. Your frame is broad and blunt, and hair with undone movement softens it exactly the right amount — while precision styling makes the same frame look rigid. The finish to chase is \"expensive but effortless.\"",
      bestStyles: [
        { name: "Long layered waves (unbrushed finish)", why: "Free-moving length balances broad shoulders without fighting them" },
        { name: "The shag or wolf cut", why: "Built-in dishevelment is the FN signature — texture reads as intentional on you" },
        { name: "Tousled lob with grown-out layers", why: "Relaxed mid-length keeps width in proportion" },
        { name: "Loose braid or undone low pony", why: "Even your \"updos\" should have escape strands — severity is the enemy" },
      ],
      avoid:
        "Helmet-polished blowouts, tight buns, and hard geometric bobs stiffen your whole look. The flat-iron is not your friend; texture spray is.",
    },
    makeup: {
      intro:
        "The best FN makeup looks like you just got back from vacation. This type suffers most under heavy, precise application — strong liner and matte full-coverage read costume-like against relaxed features.",
      tips: [
        "Skin first: tinted moisturizer or skin tint over full-coverage foundation",
        "Bronzer where the sun would hit, cream blush blended high — warmth over sculpt",
        "Brushed-up, feathery brows suit blunt bone structure better than sharp-drawn arches",
        "One coat of mascara and a balm or gloss lip finishes the look; save smoky eyes for rare nights and keep them soft-edged",
      ],
    },
    height: {
      range: "Typically 5'7\" (170 cm) and above",
      detail:
        "Flamboyant Naturals combine vertical length with width, so they read tall and substantial — most are 5'7\"+. Plenty of 5'5\"–5'6\" women land here too when broad shoulders and long limbs create a tall impression. If you have the width but clearly lack length, look at pure Natural instead.",
    },
    face: {
      intro:
        "The Flamboyant Natural face is strong but blunt: a squared or broad jaw, wide-set or heavy cheekbones, a nose with presence, and an open, friendly expression that undercuts the strength. Where a Dramatic's features point, an FN's features spread. It's a face that looks better the less is done to it.",
      tell:
        "The tell: width and bluntness instead of sharpness. Trace your jaw — if it turns a rounded corner rather than a point, and your cheekbones read wide rather than high, you're Natural family, not Dramatic.",
    },
    plusSize: {
      intro:
        "FN frames carry extra weight more gracefully than almost any other type — the width was always structural, so added softness distributes across a broad chassis without changing the silhouette's logic. The mistake plus-size FNs make is shrinking: fitted, \"flattering\" clothes that pinch a frame built for sweep.",
      styling: [
        "Scale up, never down: wide-leg trousers, longline blazers, maxi lengths — generous cuts honor a generous frame",
        "Keep shoulders unapologetic; dropped shoulders and relaxed sleeve heads fit better than set-in seams",
        "Long vertical elements (open cardigans, unbuttoned overshirts) organize width without hiding it",
        "Natural-texture fabrics — linen, suede, chunky knit — drape with the right weight; slinky thin material clings where it should sweep",
      ],
    },
    palette: [
      { name: "Warm Sand", hex: "#DDBB99" },
      { name: "Bronze", hex: "#B87F4D" },
      { name: "Sienna", hex: "#A0522D" },
      { name: "Olive", hex: "#7A6E4F" },
      { name: "Caramel Nude", hex: "#B77B57" },
    ],
    heightBar: { minCm: 170, maxCm: 190 },
  },

  natural: {
    hair: {
      intro:
        "Natural hair keeps the same rule as Natural clothing: relaxed beats arranged. Your bones are blunt but moderate, so you can wear more variety than an FN — as long as the finish never tips into stiff.",
      bestStyles: [
        { name: "Soft layers at collarbone length", why: "Movement around a moderate frame, zero fuss to maintain" },
        { name: "Air-dried natural texture", why: "Whatever your hair does on its own is usually the right answer" },
        { name: "Relaxed lob with side part", why: "Casual polish for work settings without going helmet-sleek" },
        { name: "Loose waves with a matte finish", why: "Shine-heavy glam finishes look borrowed on a Natural" },
      ],
      avoid:
        "Precision bobs, sculpted updos, and high-gloss straightening all over-formalize you. Ornate braided styles and bouncy set curls also skew costume — keep any braid loose and simple.",
    },
    makeup: {
      intro:
        "Fresh-faced is the whole strategy. Naturals look best when the makeup reads as good health rather than technique.",
      tips: [
        "Even the skin, then stop — spot conceal instead of full foundation",
        "A brushed-up brow and curled lashes do more for this face than any liner",
        "Cream blush in warm rose, applied where you actually flush",
        "Satin nude or terracotta lip; sharp-lined lips read off against soft-blunt features",
      ],
    },
    height: {
      range: "Any height — most between 5'4\" and 5'8\" (163–173 cm)",
      detail:
        "Pure Naturals sit in the moderate zone: not petite, not statuesque. The frame is blunt and slightly widened but the vertical line doesn't dominate. If you're clearly tall with long limbs, test Flamboyant Natural; if you're under 5'3\", the gamine types deserve a look before Natural does.",
    },
    face: {
      intro:
        "The Natural face is approachable strength: a gently squared jaw, broad-ish cheekbones, a nose that's straightforward rather than delicate, and eyes that read open and level. It's the \"girl next door\" face in the literal sense — friendly geometry, nothing extreme, nothing precious.",
      tell:
        "The tell: bluntness without drama. Classics have symmetry that reads formal; Naturals have symmetry that reads casual. If your face looks slightly wrong with a severe center part and slicked hair, that casualness is the diagnosis.",
    },
    plusSize: {
      intro:
        "Naturals gain weight the way they do everything — evenly and without drama. The blunt frame keeps its slightly-wide, slightly-strong character, and the relaxed silhouettes that always worked keep working at any size.",
      styling: [
        "Stay with easy, skimming shapes: straight-leg jeans, relaxed shirting, unstructured jackets",
        "Choose substantial casual fabrics (heavier cotton, twill, midweight knits) that hold a clean line over softness",
        "A long open layer adds vertical organization — think duster cardigan over a matched base",
        "Resist the urge to \"dress up\" with structure; a stiff fitted blazer fights both your bones and your ease",
      ],
    },
    palette: [
      { name: "Golden Beige", hex: "#D9B48F" },
      { name: "Soft Bronze", hex: "#C08552" },
      { name: "Muted Coral", hex: "#D98873" },
      { name: "Warm Brown", hex: "#7B5B44" },
      { name: "Cream", hex: "#F0E2D0" },
    ],
    heightBar: { minCm: 163, maxCm: 173 },
  },

  "soft-natural": {
    hair: {
      intro:
        "Soft Natural hair wants softness with a little wildness — waves over curls, movement over precision. You're balancing two messages (sturdy frame, soft flesh), and hair is the easiest place to let the yin side speak.",
      bestStyles: [
        { name: "Loose, lived-in waves", why: "The SN signature: soft curve without formality" },
        { name: "Curtain bangs with layers", why: "Rounds the face gently and suits the type's warmth" },
        { name: "Long layered cut, mid-back or shorter", why: "Enough length for softness, layered so it doesn't drag flat" },
        { name: "Textured lob with soft ends", why: "Short works if the ends stay wispy rather than blunt" },
      ],
      avoid:
        "Blunt geometric cuts and stick-straight styling erase your softness; heavily sculpted glam curls overshoot it. Very long, one-length hair also tends to pull an SN down — layers keep it lifted.",
    },
    makeup: {
      intro:
        "Golden-hour glow, in makeup form. Soft Naturals shine with warm, blended, slightly dewy application — precision is the only real enemy.",
      tips: [
        "Cream products everywhere: blush, bronzer, highlighter — powder edges read hard on soft flesh",
        "Warm peachy-pink blush on the apples, blended toward the temples",
        "Soft brown smudged liner instead of black wings",
        "Glossy or balm lips in warm rose; overlined matte lips look wrong against rounded features",
      ],
    },
    height: {
      range: "Typically up to 5'7\" (170 cm)",
      detail:
        "Soft Naturals sit at moderate height with a horizontal, slightly wide frame — the vertical line never dominates. Scarlett Johansson at 5'3\" and Jennifer Lopez at 5'5\" are both classic SNs. If you're 5'8\"+ with width and softness, compare against Flamboyant Natural before settling.",
    },
    face: {
      intro:
        "The Soft Natural face mixes blunt structure with real softness: rounded or gently squared jaw, full cheeks over wide cheekbones, a soft nose, and warm, approachable eyes. It's the face that gets called \"fresh\" and \"inviting\" — noticeably pretty without a single sharp edge doing the work.",
      tell:
        "The tell versus Romantic: width in the cheekbones and jaw underneath the softness. A Romantic face is delicate under its curves; an SN face has a sturdy, slightly wide platform under its curves.",
    },
    plusSize: {
      intro:
        "This is one of the most common real-world plus-size types, and the good news is the SN formula barely changes: soft fabrics over a relaxed-but-shaped silhouette. Weight tends to settle in the bust, upper arms, and hips while the frame's width keeps everything proportional.",
      styling: [
        "Waist emphasis, gently: soft wraps, shirring, or a mid-width belt worn relaxed — never corset-tight",
        "Flowy midi skirts and dresses with drape skim curves the way SN lines are meant to",
        "Three-quarter and flutter sleeves flatter soft arms without the coverage looking defensive",
        "Skip stiff structure and hard tailoring; if a blazer, make it soft-shouldered and worn open",
      ],
    },
    palette: [
      { name: "Peach", hex: "#E8A183" },
      { name: "Honey Gold", hex: "#D9A45B" },
      { name: "Soft Coral", hex: "#D97B6C" },
      { name: "Warm Taupe", hex: "#A08066" },
      { name: "Ivory", hex: "#F3E6D5" },
    ],
    heightBar: { minCm: 150, maxCm: 170 },
  },

  "dramatic-classic": {
    hair: {
      intro:
        "Dramatic Classic hair is polish with an edge — think boardroom, not beach. Your look is balance sharpened by a yang undercurrent, and hair that's controlled, glossy, and cleanly finished completes it.",
      bestStyles: [
        { name: "Sharp collarbone lob", why: "The DC haircut: precise line, moderate length, zero fuss" },
        { name: "Sleek straight blowout", why: "Glass finish suits your tailored aesthetic" },
        { name: "Low polished chignon", why: "Formal control that softer types can't wear without looking stern" },
        { name: "Deep side part with smooth bend", why: "Adds sophistication while keeping the line clean" },
      ],
      avoid:
        "Messy texture, shags, and beachy waves undercut your polish; extremely long hair dilutes your crispness. When in doubt, cut two inches and add gloss.",
    },
    makeup: {
      intro:
        "Precise, polished, edited. The DC face rewards technique — clean edges and deliberate color — but punishes excess. One focal point, executed perfectly.",
      tips: [
        "A precisely drawn brow is your foundation piece — sharp tail, groomed arch",
        "Tightlined or thin-winged liner keeps eyes defined without smoky heaviness",
        "Classic red lip for evening is practically your birthright; keep the edge crisp",
        "Subtle contour along cheekbones — enough to sharpen, never enough to see the technique",
      ],
    },
    height: {
      range: "Typically 5'4\"–5'7\" (163–170 cm)",
      detail:
        "Dramatic Classics read moderate with a slight vertical emphasis — taller-seeming than they measure, thanks to clean proportions. Genuinely tall women who relate to DC usually turn out Dramatic; genuinely petite ones, Flamboyant Gamine. The moderate frame is non-negotiable for this type.",
    },
    face: {
      intro:
        "The Dramatic Classic face is symmetric and even — but with a discernible edge: a tapered jaw, defined (not heavy) cheekbones, a straight nose, and composed eyes. People read this face as competent and elegant. It photographs like a news anchor's: nothing out of place, nothing soft enough to be cute.",
      tell:
        "The tell versus Soft Classic: your default expression reads authoritative rather than gentle. Versus Dramatic: nothing about your face is extreme — the sharpness is seasoning, not the dish.",
    },
    plusSize: {
      intro:
        "A plus-size Dramatic Classic keeps her balanced proportions — weight distributes fairly evenly, and the moderate frame stays moderate. The styling risk at higher weights is abandoning tailoring for shapeless comfort; DC needs its structure at every size.",
      styling: [
        "Structured-but-curved tailoring: blazers with a defined shoulder and gentle waist shaping",
        "Sheath and A-line dresses in firm, smooth fabrics hold the clean line softness can blur",
        "Vertical seaming and princess cuts organize the silhouette elegantly",
        "Keep accessories moderate and matched — chaos of scale bothers a DC look more than any body change does",
      ],
    },
    palette: [
      { name: "Merlot", hex: "#722F37" },
      { name: "Soft Plum", hex: "#8E6C88" },
      { name: "Rose Beige", hex: "#C99A94" },
      { name: "Cool Ivory", hex: "#F1E9E2" },
      { name: "Slate", hex: "#4A4A52" },
    ],
    heightBar: { minCm: 163, maxCm: 170 },
  },

  classic: {
    hair: {
      intro:
        "Classic hair should look finished — never overdone, never undone. You're the most balanced type in the system, and hair that's symmetric, smooth, and moderate in every dimension keeps that harmony intact.",
      bestStyles: [
        { name: "Chin-to-shoulder classic bob", why: "The most literal match for balanced proportions" },
        { name: "Smooth blowout with soft bend", why: "Polished movement without texture chaos" },
        { name: "French twist or neat low bun", why: "Timeless updos suit you at any formality level" },
        { name: "One-length mid-back hair, well kept", why: "Longer works when it's glossy and even, not layered wild" },
      ],
      avoid:
        "Trend-driven cuts date a Classic instantly — asymmetry, shags, bold bangs, extreme lengths in either direction. Frizz and mess cost you more than they cost any other type.",
    },
    makeup: {
      intro:
        "Balanced face, balanced makeup. The Classic strategy is even enhancement — improving everything a little rather than featuring anything a lot.",
      tips: [
        "Neutral palettes (taupe, soft brown, rose) applied with clean blending",
        "Defined but natural brows — follow your own line, don't redraw it",
        "Rose or pink-nude lipstick in a satin finish; extreme nudes and vamps both unbalance you",
        "If you emphasize eyes, quiet the lips, and vice versa — never both at once",
      ],
    },
    height: {
      range: "Typically 5'4\"–5'7\" (163–170 cm)",
      detail:
        "Classics are moderate in every measurement, height included. The type is defined by absence of extremes — so a very tall or very petite frame effectively rules it out, pushing you toward Dramatic Classic/Dramatic or the Gamine family respectively.",
    },
    face: {
      intro:
        "The Classic face is the one artists use to teach proportion: evenly spaced features, moderate everything, striking symmetry. Jaw neither sharp nor round, nose straight and proportionate, lips balanced. It's beauty that reads as \"correct\" before it reads as any particular flavor.",
      tell:
        "The tell: if you honestly can't decide whether your features are sharp or soft, large or small — that inability to pick is itself the answer. Every other type has a direction; Classic's direction is center.",
    },
    plusSize: {
      intro:
        "Weight arrives evenly on a Classic frame and the balanced proportions persist — a plus-size Classic still reads symmetric and put-together. The type's real challenge at any size is sameness, so at higher weights lean on fabric quality and fit precision rather than shape tricks.",
      styling: [
        "Invest in fit: tailored waists, correct sleeve lengths, and hemming matter more for you than anyone",
        "Wrap dresses and A-lines in firm matte fabrics keep proportions legible",
        "Matched sets and column dressing in one color preserve elegant continuity",
        "Avoid both extremes — body-con and oversized each break your balance in different directions",
      ],
    },
    palette: [
      { name: "Rose Beige", hex: "#D4A5A0" },
      { name: "Soft Red", hex: "#B03A48" },
      { name: "Neutral Taupe", hex: "#9C8A7E" },
      { name: "Ivory", hex: "#F2E8DE" },
      { name: "Dusty Pink", hex: "#C98F98" },
    ],
    heightBar: { minCm: 163, maxCm: 170 },
  },

  "soft-classic": {
    hair: {
      intro:
        "Soft Classic hair takes the Classic playbook and rounds every corner: smooth, symmetric, but with softness in the silhouette — bends instead of edges, curves instead of lines.",
      bestStyles: [
        { name: "Rounded chin-length bob", why: "Classic structure with a curved, feminine finish" },
        { name: "Soft waves at shoulder length", why: "Gentle S-bends flatter without disorder" },
        { name: "Side-swept layers around the face", why: "Softens symmetry the way your features do" },
        { name: "Loose, low romantic updo", why: "Polished but touchable — never severe" },
      ],
      avoid:
        "Sharp geometric cuts and sleek severity tip you into costume; heavy shag texture tips you into mess. Your window is narrow but lovely: keep it smooth and keep it soft.",
    },
    makeup: {
      intro:
        "Pretty is the target — softly defined, rosy, harmonious. Of all types, Soft Classic gets the most from a classic \"date night\" makeup formula and the least from editorial experiments.",
      tips: [
        "Rose and mauve tones on eyes and cheeks harmonize with soft symmetry",
        "Blended shadow with no hard liner edge — smudge everything slightly",
        "A soft pink or berry satin lip, rounded rather than sharply lined",
        "Dewy-satin skin finish; full matte flattens your gentle dimension",
      ],
    },
    height: {
      range: "Typically 5'3\"–5'6\" (160–168 cm)",
      detail:
        "Soft Classics sit at moderate height, often on the slightly shorter side of it, with balanced proportions and a soft undercurrent. Very petite women with softness usually belong in Soft Gamine or Romantic; the SC frame never reads small-and-concentrated, just moderate-and-gentle.",
    },
    face: {
      intro:
        "The Soft Classic face is balance with a sweetness setting: symmetric features where cheeks are a touch fuller, lips a touch rounder, eyes a touch larger than strict Classic proportion. It's the face casting directors pick for \"the kind one.\" Nothing is extreme; everything is gently rounded.",
      tell:
        "The tell versus Romantic: your softness sits on even, moderate proportions rather than dominating them — Romantic faces are lush first, Soft Classic faces are balanced first with softness second.",
    },
    plusSize: {
      intro:
        "A plus-size Soft Classic gains softness on an already-gentle balanced frame, so curves become more pronounced while proportions stay even. The formula holds: refined, feminine, softly structured — just executed in better fabric at larger sizes.",
      styling: [
        "Soft tailoring is the sweet spot: rounded lapels, curved seams, gentle waist definition",
        "Wrap and faux-wrap dresses in crepe or jersey honor both balance and curve",
        "Elbow or bracelet sleeves with a little drape flatter soft arms elegantly",
        "Avoid stiff boxy jackets and aggressive body-con alike — stay in the soft middle",
      ],
    },
    palette: [
      { name: "Rose", hex: "#D08C97" },
      { name: "Mauve", hex: "#A87B92" },
      { name: "Berry Satin", hex: "#A44E68" },
      { name: "Peach Cream", hex: "#EFC9B4" },
      { name: "Soft Taupe", hex: "#9C8578" },
    ],
    heightBar: { minCm: 160, maxCm: 168 },
  },

  "flamboyant-gamine": {
    hair: {
      intro:
        "Flamboyant Gamine hair is where you get to be loud. This type thrives on deliberate contrast and crisp edges — the haircut is often the single most recognizable thing about an FG, and playing it safe wastes the type's best feature.",
      bestStyles: [
        { name: "Choppy pixie", why: "Concentrated, sharp, and playful — the FG signature since Audrey and Twiggy" },
        { name: "Asymmetric or A-line bob", why: "Built-in contrast mirrors your yin-yang juxtaposition" },
        { name: "Micro-fringe with a crop", why: "Bold graphic bangs only work this well on gamine faces" },
        { name: "Sleek bob with blunt bangs", why: "Mod-era geometry suits your compact scale" },
      ],
      avoid:
        "Long, flowing, romantic hair swallows an FG whole — it blurs your crispness and drags your compact frame down. Soft beach waves do the same in a smaller way. Short, sharp, intentional: that's the lane.",
    },
    makeup: {
      intro:
        "Pick one bold thing and commit. FG faces carry graphic statements — a red lip, a cat eye, even colored liner — better than any type, but layered full glam crowds small-scale features.",
      tips: [
        "A single graphic element: bold wing OR bright lip OR strong brow — one, executed sharply",
        "Bright saturated lip colors (true red, fuchsia) look intentional, not overdone, on you",
        "Defined, slightly angular brows frame mixed features well",
        "Keep skin fresh and blush minimal — the statement piece needs quiet around it",
      ],
    },
    height: {
      range: "Typically 5'5\" (165 cm) and under",
      detail:
        "FGs are petite with a leggy, angular quality that can read taller in photos than in person. Twiggy at 5'6\" is the famous edge case. If you're 5'8\" and drawn to FG's crisp mix, you're usually looking at a Dramatic or Dramatic Classic who likes gamine styling — the type needs genuinely small scale.",
    },
    face: {
      intro:
        "The Flamboyant Gamine face is a deliberate argument: large eyes against sharp cheekbones, a pointed chin under full-ish lips, features that are individually bold crowded onto a small canvas. It's animated, expressive, and photographs \"interesting\" rather than conventionally pretty — which is precisely its power.",
      tell:
        "The tell: contrast you can point at. Where a Classic face agrees with itself, an FG face visibly mixes sharp and soft, large and small — and the sharp side leads.",
    },
    plusSize: {
      intro:
        "A plus-size FG keeps her compact, angular scale — bones don't grow — and the styling rule survives intact: crisp beats flowy at every size. The trap is hiding in oversized layers, which erase the very juxtaposition that makes this type work.",
      styling: [
        "Keep silhouettes cropped and defined: fitted jackets ending at the hip, tapered trousers, structured minis",
        "Break up the body deliberately — color blocking and contrast pieces suit you better than one long column",
        "Small-scale sharp details (crisp collars, geometric jewelry) keep the look intentional",
        "Avoid maxi lengths and billowing volume; even plus-size FGs look best in clothes that end somewhere definite",
      ],
    },
    palette: [
      { name: "Cherry Red", hex: "#C21E3A" },
      { name: "Fuchsia", hex: "#C74B8B" },
      { name: "Graphite", hex: "#43454B" },
      { name: "Clear Coral", hex: "#E5654F" },
      { name: "Nude Beige", hex: "#E3C0A8" },
    ],
    heightBar: { minCm: 145, maxCm: 165 },
  },

  gamine: {
    hair: {
      intro:
        "Gamine hair is short more often than any other type's, and for good reason: a compact frame with evenly mixed yin-yang reads freshest when the hair is light, cropped, and a little mischievous.",
      bestStyles: [
        { name: "The classic gamine crop", why: "Jean Seberg made it the type's calling card — light, short, effortless" },
        { name: "Soft pixie with texture", why: "Playful movement on a small scale" },
        { name: "Chin bob with light layers", why: "The longest length that keeps gamine energy intact" },
        { name: "Short cut with baby bangs", why: "Highlights bright eyes and an animated face" },
      ],
      avoid:
        "Long heavy hair is the classic gamine mistake — it drowns a petite mixed frame and reads \"borrowed.\" Equally skip formal sculpted styling; gamine hair should look like you ran your hands through it and got lucky.",
    },
    makeup: {
      intro:
        "Fresh with one crisp accent. The gamine face is naturally animated — makeup's job is to sharpen the sparkle, not add weight.",
      tips: [
        "A quick liner flick or tightline wakes up the eyes without heaviness",
        "Cream blush placed high for a fresh, alert look",
        "Groomed, natural brows — overly drawn brows overpower small features",
        "Sheer bright lip (poppy, rosy-red) over full matte coverage",
      ],
    },
    height: {
      range: "Typically 5'5\" (165 cm) and under",
      detail:
        "Gamines are genuinely petite — the type's essence depends on small scale with mixed features. Above 5'5\", the same feature mix usually resolves into Natural or Classic types instead. Petite frame, boyish-to-mixed proportions, quick energy: that's the package.",
    },
    face: {
      intro:
        "The Gamine face mixes sharp and soft in near-equal measure on a small scale: bright, large-ish eyes, a neat pointed chin, features that flicker between delicate and crisp as expression changes. It's the most animated face in the system — \"elfin\" gets used a lot, and earns it.",
      tell:
        "The tell versus both gamine siblings: no single force wins. FG faces lead sharp, SG faces lead soft; if your features genuinely alternate and neither reads dominant, you're the rare true Gamine.",
    },
    plusSize: {
      intro:
        "A plus-size Gamine stays small-boned and compact — weight rounds the silhouette but the short vertical line and quick proportions remain. Styling still wants definition and lightness rather than coverage.",
      styling: [
        "Defined shapes in light fabrics: fitted crops, neat cardigans, tapered or straight cropped pants",
        "Keep hemlines and sleeves ending at deliberate points — ankle, wrist, hip — not flowing past them",
        "Mix, don't match: gamine outfits at any size benefit from playful contrast",
        "Skip long duster layers and maxi dresses, which bury a petite mixed frame",
      ],
    },
    palette: [
      { name: "Poppy", hex: "#D6494A" },
      { name: "Soft Black", hex: "#3A3A3C" },
      { name: "Petal Pink", hex: "#E9B4BC" },
      { name: "Warm Nude", hex: "#D9AE94" },
      { name: "Brick", hex: "#A8574A" },
    ],
    heightBar: { minCm: 145, maxCm: 165 },
  },

  "soft-gamine": {
    hair: {
      intro:
        "Soft Gamine hair is short-to-mid with movement — the cut should be as cute and concentrated as the type itself. You're petite with rounded yin leading the mix, so the ideal style has curve, bounce, and a defined shape all at once.",
      bestStyles: [
        { name: "Curly or wavy pixie", why: "Soft texture in a cropped shape is peak SG" },
        { name: "Chin-length bob with bend", why: "Rounded silhouette that frames doll-like features" },
        { name: "Shoulder-length with bouncy layers", why: "The practical maximum — keep visible spring in it" },
        { name: "Short shag with soft bangs", why: "Playful volume that matches SG energy" },
      ],
      avoid:
        "Long straight curtains of hair flatten your sparkle and swamp your scale. Severe slicked styles read harsh against rounded features. If your hair is long for now, wear it up high and full rather than hanging.",
    },
    makeup: {
      intro:
        "The SG face was built for what the beauty industry now sells as \"douyin\" or doll makeup — rounded, bright-eyed, fresh. Lean in; it looks costume-ish on other types and native on you.",
      tips: [
        "Cream blush on the apples of the cheeks — round placement, not sculpted",
        "Curled lashes and defined lower lash line open already-large eyes",
        "Berry and rosy-pink lips with a soft, slightly rounded application",
        "Skip harsh contour entirely; a touch of highlighter on cheek-tops does the lifting",
      ],
    },
    height: {
      range: "Typically 5'4\" (163 cm) and under",
      detail:
        "Soft Gamines are petite — small bones, short-to-moderate vertical line, rounded flesh. At 5'6\"+ with softness and sharpness mixed, you're more likely Soft Classic or Soft Natural. The SG impression is concentrated: a lot of feature in a small frame.",
    },
    face: {
      intro:
        "The Soft Gamine face is the system's most openly \"cute\": big rounded eyes, full cheeks, a small tapered chin, and full lips — yin abundance with just enough sharpness (that chin, those cheekbones) to keep it lively rather than languid. It reads years younger than it is, reliably and permanently.",
      tell:
        "The tell versus Romantic: sharpness interrupting the softness — a pointed chin, crisp brow, or pert nose that a true Romantic face wouldn't have. Versus FG: your roundness leads; theirs answers to sharp.",
    },
    plusSize: {
      intro:
        "Plus-size Soft Gamines keep the small frame and the concentrated cuteness — weight adds roundness to a type that was already built on it, so curves intensify while scale stays petite. Definition remains the entire game.",
      styling: [
        "Fit-and-flare everything: defined waist, flippy skirt, ends above the ankle",
        "Cropped cardigans and jackets keep proportions high and lively",
        "Sweetheart and scoop necklines suit rounded features at any size",
        "Avoid oversized shapeless layers — an SG in a tent loses both her waist and her spark",
      ],
    },
    palette: [
      { name: "Candy Pink", hex: "#DE7C97" },
      { name: "Warm Rose", hex: "#C96D7E" },
      { name: "Peach", hex: "#EFAF93" },
      { name: "Soft Brown", hex: "#6E5647" },
      { name: "Berry Pop", hex: "#B04A6E" },
    ],
    heightBar: { minCm: 145, maxCm: 163 },
  },

  "theatrical-romantic": {
    hair: {
      intro:
        "Theatrical Romantic hair is glamour at a small scale: defined curls, glossy waves, everything deliberate and a little sultry. The delicate sharpness in your features rewards precision that pure Romantics don't need.",
      bestStyles: [
        { name: "Defined glossy curls", why: "Sculpted curve — soft but intentional, like the type itself" },
        { name: "Vintage waves (30s/40s style)", why: "Old-Hollywood siren styling was drawn on TR bone structure" },
        { name: "Long layers with face-framing pieces", why: "Delicate framing highlights sharp small features" },
        { name: "Side-parted deep wave bob", why: "Compact glamour when you want short" },
      ],
      avoid:
        "Undone texture and messy buns read sloppy against refined features, and blunt sporty cuts erase your romance. TR hair should always look finished — even the \"effortless\" version.",
    },
    makeup: {
      intro:
        "Sultry and precise. The TR face — lush features with a sharp finish — carries seductive makeup better than any other type; the same look reads heavy on soft faces and harsh on blunt ones.",
      tips: [
        "A winged liner with a fine, sharp tail plays to your delicate edge",
        "Defined lips in rich color — dusty rose to deep berry — with a crisp lip line",
        "Jewel-toned shadow (plum, bronze, emerald) suits intense small features",
        "Sculpt subtly: a whisper of contour under the cheekbone sharpens without hardening",
      ],
    },
    height: {
      range: "Typically 5'4\" (163 cm) and under",
      detail:
        "Theatrical Romantics are petite with pronounced curve and a delicate sharpness in the bones. Salma Hayek at 5'2\" is the modern reference. A taller woman with the same lush-plus-sharp mix is usually a Soft Dramatic — scale is the dividing line between those two types.",
    },
    face: {
      intro:
        "The Theatrical Romantic face is lushness with a fine edge: large liquid eyes, full lips, soft cheeks — set against a delicately tapered chin, a defined nose, or subtly sharp cheekbones. It's the femme fatale face: romantic at first glance, precise on the second.",
      tell:
        "The tell versus Romantic: at least one genuinely sharp detail (nose, chin, or cheekbone) breaking the softness. Versus Soft Gamine: your energy is sultry rather than cute — TR features smolder where SG features sparkle.",
    },
    plusSize: {
      intro:
        "Plus-size TRs keep their defining combination — pronounced hourglass curve on a small, delicate frame. Weight amplifies the curve, and the styling answer is the same as it always was: honor the waist, drape the curve, keep details refined.",
      styling: [
        "Curve-following silhouettes: wrap dresses, sweetheart necklines, bias-cut skirts",
        "Waist definition is non-negotiable — soft belts, seamed waists, corsetry details in comfortable form",
        "Delicate ornamentation (fine jewelry, lace edges, small prints) matches your scale better than chunky statement pieces",
        "Avoid stiff boxy cuts and heavy fabrics that bypass the waist; they erase the silhouette that defines you",
      ],
    },
    palette: [
      { name: "Wine", hex: "#7E2A44" },
      { name: "Rose Gold", hex: "#D8A292" },
      { name: "Deep Rose", hex: "#B85C74" },
      { name: "Ivory Sheen", hex: "#F3E3DA" },
      { name: "Smoked Plum", hex: "#6B4A5E" },
    ],
    heightBar: { minCm: 145, maxCm: 163 },
  },

  romantic: {
    hair: {
      intro:
        "Romantic hair is soft everything: soft curl, soft volume, soft edges. Your face and body are pure yin, and hair with any hardness — blunt lines, slick finishes — instantly looks like it belongs to someone else.",
      bestStyles: [
        { name: "Long soft curls or waves", why: "Rounded, flowing shape mirrors the double-curve silhouette" },
        { name: "Layered cut with rounded ends", why: "Layers keep lush hair light; rounded ends keep it yin" },
        { name: "Romantic updo with loose tendrils", why: "Formal styling should still have escape softness" },
        { name: "Side-swept Old-Hollywood wave", why: "Marilyn-era glamour built for this exact face" },
      ],
      avoid:
        "Blunt bobs, sharp pixies, and poker-straight styling contradict every line you have. Even a slicked ponytail is risky — if hair goes up, it should go up soft, with volume left around the face.",
    },
    makeup: {
      intro:
        "Soft glamour, rounded application. The Romantic face takes blended, luminous makeup beautifully and rejects anything graphic — a hard wing on rounded eyes always looks slightly off.",
      tips: [
        "Rounded, diffused shadow — halo eyes rather than sharp cut creases",
        "Lush lashes and soft liner smudged at the edges",
        "Pink, rose, and berry lips with a soft blotted or glossy finish",
        "Luminous skin with cream blush placed on the roundest part of the cheek",
      ],
    },
    height: {
      range: "Typically 5'5\" (165 cm) and under",
      detail:
        "Romantics are petite-to-moderate with a short vertical line and delicate bones — the double-curve silhouette needs a compact canvas. Height is one of the fastest ways to rule the type out: at 5'7\"+ with lush curves, Soft Dramatic is almost always the real answer.",
    },
    face: {
      intro:
        "The Romantic face is roundness all the way down: large soft eyes, full cheeks, a gently rounded jaw and chin, full curved lips. No feature interrupts another; everything flows. It's the face that has meant \"feminine beauty\" in Western portraiture for a few hundred years, and it ages remarkably gently.",
      tell:
        "The tell: search your face for a single sharp line and come up empty. TRs keep one; Soft Gamines keep a pointed chin; Romantics have none — softness without exception.",
    },
    plusSize: {
      intro:
        "Romantic is arguably the type plus-size styling advice serves worst — mainstream tips push structure and coverage, which are exactly wrong here. A plus-size Romantic has more of what the type is built on (curve), and needs the same answer at a larger scale: drape, waist, softness.",
      styling: [
        "Draped, fluid fabrics that follow curve — jersey, crepe, charmeuse — over anything stiff",
        "V and sweetheart necklines with soft gathering flatter a full bust",
        "Define the waist softly with wraps and seaming; skipping the waist adds visual weight everywhere",
        "Ignore \"hide it in black boxes\" advice entirely — shapeless clothing makes a Romantic look larger, not smaller",
      ],
    },
    palette: [
      { name: "Rose Petal", hex: "#DD8FA3" },
      { name: "Warm Blush", hex: "#E7AFB2" },
      { name: "Berry Stain", hex: "#A6486A" },
      { name: "Champagne", hex: "#EED9C4" },
      { name: "Soft Cocoa", hex: "#8C6A5D" },
    ],
    heightBar: { minCm: 145, maxCm: 165 },
  },
};
